import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth, API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { 
  Users, Plus, Trash2, Edit, ArrowLeft, Mail, Phone, Check, X, 
  Loader2, UserPlus, Clock, Send, AlertCircle, CheckCircle2, Building
} from 'lucide-react';
import { toast } from 'sonner';
import Header from '../components/Header';

const SPECIALTIES = [
  { value: 'marble', label: 'Marmo' },
  { value: 'granite', label: 'Granito' },
  { value: 'quartzite', label: 'Quarzite' },
  { value: 'travertine', label: 'Travertino' },
  { value: 'onyx', label: 'Onice' },
  { value: 'limestone', label: 'Pietra calcarea' },
  { value: 'general', label: 'Generale' }
];

const AdvisorGroupsPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  
  // Dialog states
  const [showGroupDialog, setShowGroupDialog] = useState(false);
  const [showAdvisorDialog, setShowAdvisorDialog] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [savingGroup, setSavingGroup] = useState(false);
  const [savingAdvisor, setSavingAdvisor] = useState(false);
  
  // Form states
  const [groupForm, setGroupForm] = useState({
    name: '',
    description: '',
    specialty: 'general',
    email_distribution: true
  });
  
  const [advisorForm, setAdvisorForm] = useState({
    name: '',
    email: '',
    phone: '',
    specialty: '',
    bio: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [groupsRes, schedulerRes] = await Promise.all([
        axios.get(`${API}/advisor-groups`, { withCredentials: true }),
        axios.get(`${API}/advisor-groups/scheduler/status`, { withCredentials: true }).catch(() => ({ data: null }))
      ]);
      setGroups(groupsRes.data);
      setSchedulerStatus(schedulerRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  const fetchGroupDetails = async (groupId) => {
    try {
      const res = await axios.get(`${API}/advisor-groups/${groupId}`, { withCredentials: true });
      setSelectedGroup(res.data);
    } catch (error) {
      toast.error('Errore nel caricamento del gruppo');
    }
  };

  const handleGroupSubmit = async () => {
    setSavingGroup(true);
    try {
      if (editingGroup) {
        await axios.put(`${API}/advisor-groups/${editingGroup.group_id}`, groupForm, { withCredentials: true });
        toast.success('Gruppo aggiornato');
      } else {
        await axios.post(`${API}/advisor-groups`, groupForm, { withCredentials: true });
        toast.success('Gruppo creato');
      }
      setShowGroupDialog(false);
      setEditingGroup(null);
      resetGroupForm();
      fetchData();
    } catch (error) {
      toast.error('Errore nel salvataggio');
    } finally {
      setSavingGroup(false);
    }
  };

  const handleAdvisorSubmit = async () => {
    if (!selectedGroup) return;
    setSavingAdvisor(true);
    try {
      await axios.post(`${API}/advisor-groups/${selectedGroup.group_id}/advisors`, advisorForm, { withCredentials: true });
      toast.success('Advisor aggiunto');
      setShowAdvisorDialog(false);
      resetAdvisorForm();
      fetchGroupDetails(selectedGroup.group_id);
      fetchData();
    } catch (error) {
      toast.error('Errore nel salvataggio');
    } finally {
      setSavingAdvisor(false);
    }
  };

  const deleteGroup = async (groupId) => {
    if (!window.confirm('Eliminare questo gruppo? Tutti gli advisor verranno disattivati.')) return;
    try {
      await axios.delete(`${API}/advisor-groups/${groupId}`, { withCredentials: true });
      toast.success('Gruppo eliminato');
      setSelectedGroup(null);
      fetchData();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const removeAdvisor = async (advisorId) => {
    if (!selectedGroup || !window.confirm('Rimuovere questo advisor dal gruppo?')) return;
    try {
      await axios.delete(`${API}/advisor-groups/${selectedGroup.group_id}/advisors/${advisorId}`, { withCredentials: true });
      toast.success('Advisor rimosso');
      fetchGroupDetails(selectedGroup.group_id);
      fetchData();
    } catch (error) {
      toast.error('Errore nella rimozione');
    }
  };

  const sendVerificationEmail = async (advisorId) => {
    try {
      await axios.post(`${API}/advisor-groups/advisors/${advisorId}/verify`, {}, { withCredentials: true });
      toast.success('Email di verifica inviata');
      if (selectedGroup) {
        fetchGroupDetails(selectedGroup.group_id);
      }
    } catch (error) {
      toast.error('Errore nell\'invio dell\'email');
    }
  };

  const triggerTrendCalculation = async () => {
    try {
      toast.info('Calcolo trend in corso...');
      const res = await axios.post(`${API}/advisor-groups/scheduler/run-trends`, {}, { withCredentials: true });
      toast.success(`Trend calcolati! ${res.data.materials_analyzed} materiali analizzati`);
    } catch (error) {
      toast.error('Errore nel calcolo dei trend');
    }
  };

  const resetGroupForm = () => {
    setGroupForm({ name: '', description: '', specialty: 'general', email_distribution: true });
  };

  const resetAdvisorForm = () => {
    setAdvisorForm({ name: '', email: '', phone: '', specialty: '', bio: '' });
  };

  const openEditGroup = (group) => {
    setEditingGroup(group);
    setGroupForm({
      name: group.name,
      description: group.description || '',
      specialty: group.specialty,
      email_distribution: group.email_distribution
    });
    setShowGroupDialog(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="max-w-[1400px] mx-auto px-6 md:px-12 py-12 pt-28">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
              <Link to="/admin" className="hover:text-foreground">Admin</Link>
              <span>/</span>
              <span>Advisor Groups</span>
            </div>
            <h1 className="heading-display text-3xl font-medium">Gestione Advisor Groups</h1>
            <p className="text-muted-foreground mt-1">
              Organizza gli advisor per specialità e configura la distribuzione email
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <Button 
              variant="outline" 
              onClick={triggerTrendCalculation}
              data-testid="trigger-trends-btn"
            >
              <Clock className="w-4 h-4 mr-2" />
              Calcola Trend
            </Button>
            <Dialog open={showGroupDialog} onOpenChange={setShowGroupDialog}>
              <DialogTrigger asChild>
                <Button onClick={() => { setEditingGroup(null); resetGroupForm(); }} data-testid="create-group-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Nuovo Gruppo
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                  <DialogTitle>{editingGroup ? 'Modifica Gruppo' : 'Crea Nuovo Gruppo'}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div>
                    <Label htmlFor="group-name">Nome Gruppo</Label>
                    <Input
                      id="group-name"
                      value={groupForm.name}
                      onChange={(e) => setGroupForm(f => ({ ...f, name: e.target.value }))}
                      placeholder="es. Esperti Marmo"
                      className="mt-1"
                      data-testid="group-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="group-specialty">Specialità</Label>
                    <Select 
                      value={groupForm.specialty} 
                      onValueChange={(v) => setGroupForm(f => ({ ...f, specialty: v }))}
                    >
                      <SelectTrigger className="mt-1" data-testid="group-specialty-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {SPECIALTIES.map(s => (
                          <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="group-description">Descrizione</Label>
                    <Textarea
                      id="group-description"
                      value={groupForm.description}
                      onChange={(e) => setGroupForm(f => ({ ...f, description: e.target.value }))}
                      placeholder="Descrivi il focus del gruppo..."
                      className="mt-1"
                      rows={3}
                    />
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <div>
                      <p className="font-medium">Distribuzione Email</p>
                      <p className="text-sm text-muted-foreground">
                        Invia email a tutti gli advisor quando arriva una richiesta
                      </p>
                    </div>
                    <Switch
                      checked={groupForm.email_distribution}
                      onCheckedChange={(v) => setGroupForm(f => ({ ...f, email_distribution: v }))}
                      data-testid="group-email-toggle"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowGroupDialog(false)}>Annulla</Button>
                  <Button onClick={handleGroupSubmit} disabled={savingGroup || !groupForm.name} data-testid="save-group-btn">
                    {savingGroup && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    {editingGroup ? 'Salva Modifiche' : 'Crea Gruppo'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Scheduler Status */}
        {schedulerStatus && (
          <div className="mb-8 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              <span className="font-medium text-emerald-900">Scheduler Attivo</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              {schedulerStatus.jobs?.map(job => (
                <div key={job.id} className="text-emerald-700">
                  <span className="font-medium">{job.name}:</span>{' '}
                  {job.next_run ? new Date(job.next_run).toLocaleString('it-IT') : 'N/A'}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Groups List */}
          <div className="lg:col-span-1">
            <div className="bg-white border border-border rounded-lg">
              <div className="p-4 border-b border-border">
                <h2 className="font-medium">Gruppi ({groups.length})</h2>
              </div>
              <div className="divide-y divide-border">
                {groups.length === 0 ? (
                  <div className="p-8 text-center text-muted-foreground">
                    <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Nessun gruppo creato</p>
                  </div>
                ) : (
                  groups.map(group => (
                    <button
                      key={group.group_id}
                      onClick={() => fetchGroupDetails(group.group_id)}
                      className={`w-full p-4 text-left hover:bg-secondary/50 transition-colors ${
                        selectedGroup?.group_id === group.group_id ? 'bg-secondary' : ''
                      }`}
                      data-testid={`group-item-${group.group_id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{group.name || group.group_name}</p>
                          <p className="text-sm text-muted-foreground">
                            {SPECIALTIES.find(s => s.value === (group.specialty || group.material_category))?.label || group.specialty || group.material_category}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-muted-foreground">{group.advisor_count || 0}</span>
                          <Users className="w-4 h-4 text-muted-foreground" />
                        </div>
                      </div>
                      {(group.email_distribution || group.distribution_email) && (
                        <div className="flex items-center gap-1 mt-2 text-xs text-emerald-600">
                          <Mail className="w-3 h-3" />
                          <span>Email attive</span>
                        </div>
                      )}
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Group Details */}
          <div className="lg:col-span-2">
            {selectedGroup ? (
              <div className="bg-white border border-border rounded-lg">
                <div className="p-6 border-b border-border">
                  <div className="flex items-start justify-between">
                    <div>
                      <h2 className="text-xl font-medium">{selectedGroup.name || selectedGroup.group_name}</h2>
                      <p className="text-muted-foreground mt-1">
                        {SPECIALTIES.find(s => s.value === (selectedGroup.specialty || selectedGroup.material_category))?.label || selectedGroup.specialty || selectedGroup.material_category}
                        {(selectedGroup.description || selectedGroup.group_description) && ` • ${selectedGroup.description || selectedGroup.group_description}`}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={() => openEditGroup(selectedGroup)}>
                        <Edit className="w-4 h-4 mr-1" /> Modifica
                      </Button>
                      <Button variant="destructive" size="sm" onClick={() => deleteGroup(selectedGroup.group_id)}>
                        <Trash2 className="w-4 h-4 mr-1" /> Elimina
                      </Button>
                    </div>
                  </div>
                  
                  {(selectedGroup.email_distribution || selectedGroup.distribution_email) && (
                    <div className="mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center gap-2">
                      <Mail className="w-4 h-4 text-emerald-600" />
                      <span className="text-sm text-emerald-700">
                        Le richieste vengono inviate via email a tutti gli advisor del gruppo
                      </span>
                    </div>
                  )}
                </div>

                {/* Advisors */}
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-medium">Advisor ({selectedGroup.advisors?.length || 0})</h3>
                    <Dialog open={showAdvisorDialog} onOpenChange={setShowAdvisorDialog}>
                      <DialogTrigger asChild>
                        <Button size="sm" onClick={resetAdvisorForm} data-testid="add-advisor-btn">
                          <UserPlus className="w-4 h-4 mr-1" /> Aggiungi Advisor
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Aggiungi Advisor a {selectedGroup.name}</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                          <div>
                            <Label htmlFor="advisor-name">Nome Completo *</Label>
                            <Input
                              id="advisor-name"
                              value={advisorForm.name}
                              onChange={(e) => setAdvisorForm(f => ({ ...f, name: e.target.value }))}
                              placeholder="Mario Rossi"
                              className="mt-1"
                              data-testid="advisor-name-input"
                            />
                          </div>
                          <div>
                            <Label htmlFor="advisor-email">Email *</Label>
                            <Input
                              id="advisor-email"
                              type="email"
                              value={advisorForm.email}
                              onChange={(e) => setAdvisorForm(f => ({ ...f, email: e.target.value }))}
                              placeholder="mario@azienda.com"
                              className="mt-1"
                              data-testid="advisor-email-input"
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                              Verrà inviata un'email di verifica
                            </p>
                          </div>
                          <div>
                            <Label htmlFor="advisor-phone">Telefono</Label>
                            <Input
                              id="advisor-phone"
                              value={advisorForm.phone}
                              onChange={(e) => setAdvisorForm(f => ({ ...f, phone: e.target.value }))}
                              placeholder="+39 333 1234567"
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label htmlFor="advisor-bio">Bio / Note</Label>
                            <Textarea
                              id="advisor-bio"
                              value={advisorForm.bio}
                              onChange={(e) => setAdvisorForm(f => ({ ...f, bio: e.target.value }))}
                              placeholder="Esperienza, specializzazioni..."
                              className="mt-1"
                              rows={3}
                            />
                          </div>
                        </div>
                        <DialogFooter>
                          <Button variant="outline" onClick={() => setShowAdvisorDialog(false)}>Annulla</Button>
                          <Button 
                            onClick={handleAdvisorSubmit} 
                            disabled={savingAdvisor || !advisorForm.name || !advisorForm.email}
                            data-testid="save-advisor-btn"
                          >
                            {savingAdvisor && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            Aggiungi Advisor
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </div>

                  {!selectedGroup.advisors?.length ? (
                    <div className="text-center py-12 bg-secondary/30 rounded-lg">
                      <Users className="w-12 h-12 mx-auto mb-3 text-muted-foreground" />
                      <p className="text-muted-foreground">Nessun advisor in questo gruppo</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Aggiungi advisor per ricevere richieste via email
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {selectedGroup.advisors.map(advisor => (
                        <div 
                          key={advisor.advisor_id} 
                          className="p-4 border border-border rounded-lg"
                          data-testid={`advisor-card-${advisor.advisor_id}`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-3">
                              <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                                <span className="text-primary font-medium">
                                  {advisor.name?.charAt(0).toUpperCase()}
                                </span>
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <p className="font-medium">{advisor.name}</p>
                                  {advisor.email_verified ? (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs rounded-full">
                                      <CheckCircle2 className="w-3 h-3" /> Verificato
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full">
                                      <AlertCircle className="w-3 h-3" /> Non verificato
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                                  <span className="flex items-center gap-1">
                                    <Mail className="w-3 h-3" /> {advisor.email}
                                  </span>
                                  {advisor.phone && (
                                    <span className="flex items-center gap-1">
                                      <Phone className="w-3 h-3" /> {advisor.phone}
                                    </span>
                                  )}
                                </div>
                                {advisor.bio && (
                                  <p className="text-sm text-muted-foreground mt-2">{advisor.bio}</p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {!advisor.email_verified && (
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => sendVerificationEmail(advisor.advisor_id)}
                                >
                                  <Send className="w-3 h-3 mr-1" /> Invia Verifica
                                </Button>
                              )}
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="text-destructive hover:text-destructive"
                                onClick={() => removeAdvisor(advisor.advisor_id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-white border border-border rounded-lg p-12 text-center">
                <Users className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
                <h2 className="text-xl font-medium mb-2">Seleziona un Gruppo</h2>
                <p className="text-muted-foreground">
                  Seleziona un gruppo dalla lista per vedere i dettagli e gestire gli advisor
                </p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdvisorGroupsPage;
